import { FC, useEffect, useRef } from 'react';

import { Box } from '@bonusly/design-system';

import { DOODLE_CANVAS, DOODLE_CANVAS_BG } from './constants';
import { renderStrokeToCtx } from './stroke-utils';
import type { DoodleStroke } from './use-doodle';

const CANVAS_WIDTH = DOODLE_CANVAS.width;
const CANVAS_HEIGHT = DOODLE_CANVAS.height;

export interface DoodlePreviewProps {
	/** The backend-generated `doodle.strokes` to render read-only. */
	strokes: DoodleStroke[];
	/** When provided, the preview is a button that opens the editor on click. */
	onClick?: () => void;
	ariaLabel?: string;
}

/**
 * Read-only render of a generated doodle so it is visible inline on the draft
 * card before it is attached. Shares the stroke renderer with the editor canvas
 * so the preview matches exactly what the modal shows.
 */
export const DoodlePreview: FC<DoodlePreviewProps> = ({ strokes, onClick, ariaLabel }) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.fillStyle = DOODLE_CANVAS_BG;
		ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
		for (const stroke of strokes) {
			renderStrokeToCtx(ctx, stroke);
		}
	}, [strokes]);

	return (
		<Box
			as={onClick ? 'button' : undefined}
			type={onClick ? 'button' : undefined}
			onClick={onClick}
			aria-label={ariaLabel}
			display="block"
			w="100%"
			maxW="360px"
			p={0}
			bg="white"
			borderRadius="lg"
			overflow="hidden"
			border="1px solid"
			borderColor="border.default"
			cursor={onClick ? 'pointer' : 'default'}
		>
			<canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={{ display: 'block', width: '100%', height: 'auto' }} />
		</Box>
	);
};
